import { useState } from 'react';
import {
  Checkbox,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { useParams } from 'react-router';
import type { PtwOnayBasamagi, PtwPermitDetail, PtwViolationSummary } from '@isg/shared-types';
import {
  useIhlalKarari,
  useIzinDetay,
  useCiktiIndir,
  useIzinIslemi,
  useIzinJsa,
  useIzinKatilimcilari,
  useAktifKatilimciEkle,
  useKatilimciKarari,
  useIzinSecenekleri,
  useIzinSorumlusu,
  useIzinUygunluk,
} from '../api/queries';
import type { PtwIzinIslemi } from '../api/queries';
import {
  PTW_GERI_CEKILME_KAYNAK_ETIKET,
  PTW_ISLEM_ETIKET,
  PTW_IZOLASYON_DURUM_ETIKET,
  PTW_BASAMAK_DURUM_ETIKET,
  PTW_ONAY_KAPI_ETIKET,
  PTW_ONAY_SONUC_ETIKET,
  PTW_SUPER_DURUM_ETIKET,
  SAPKA_ETIKET,
} from '../i18n/tr';
import {
  GecerlilikHucresi,
  KunyeTablosu,
  OnaylaDugmesi,
  UygunlukListesi,
  damga,
  durumRozeti,
  kisiEtiketi,
} from './izin-parts';
import { IzinTuruFormBolumu } from './izin-form-parts';
import { BrifingBolumu } from './izin-brifing-parts';
import { KunyeDuzenleyici } from './izin-kunye-parts';
import { QueryStatus } from './parts';
import { PTW_ITA_KILIT_METNI } from '@isg/validation';
import { Bolum, KapiSebebi, Rozet, SatirEylemleri, SolukSatir, YazamamaSebebi } from '../sozlesme';
import { KararKutusu } from './BekleyenOnaylarScreen';
import type { KararSecimi } from './BekleyenOnaylarScreen';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import { ANLAM } from '../theme';
import { zamanGoster } from '../lib/format';

/**
 * İZİN DETAYI (F3·S4) — talebin tamamı + iz kayıtları + aksiyonlar.
 *
 * DÜĞMELERİN GÖRÜNÜRLÜĞÜ SUNUCUDAN: her düğme `detay.yetkiler` alanına
 * bakar; ekran "kim ne yapabilir"i YENİDEN HESAPLAMAZ (§2.16). Yetki cevabı
 * motorun kendi yüklemlerinden üretilir, dolayısıyla görünmeyen düğmenin ucu
 * da 403/409 verir — iki kaynak yok.
 *
 * SALT OKUNUR BÖLÜMLER: izolasyon/kilit durumu burada yalnız GÖSTERİLİR;
 * saha aksiyonları (askı · devir · LOTO · "işim bitti" · saha kontrolü)
 * S5'in mobil/saha yüzeyidir.
 */
export function IzinDetayScreen(): React.JSX.Element {
  const { permitId } = useParams();
  const detay = useIzinDetay(permitId ?? null);
  const uygunluk = useIzinUygunluk(permitId ?? null);

  return (
    <Stack>
      <QueryStatus queries={[detay]} kayit />
      {detay.isSuccess && (
        <>
          <Group justify="space-between" align="start">
            <Stack gap={4}>
              <Title order={3}>
                {detay.data.permit.workplaceAd} — {detay.data.permit.locationAd}
              </Title>
              <Group gap="xs">
                {durumRozeti(detay.data.permit)}
                <Badge variant="outline">
                  {PTW_SUPER_DURUM_ETIKET[detay.data.permit.superDurum]}
                </Badge>
                {damga(detay.data.permit)}
              </Group>
            </Stack>
            <IslemDugmeleri detay={detay.data} />
          </Group>

          {/* ✏️ 28.09.2026 (K-T1): KÜNYE TASLAKTA DÜZENLENİR ve katılımcı
              kartı AYNI YÜZEYDEDİR. P-1'den beri katılımcı taslakta
              düzenlenebiliyordu ama kart künyenin DIŞINDA duruyordu; künye
              salt okunur çizildiği için kullanıcı katılımcıyı da düzenlenemez
              sanıyordu (demo görüşmesi 25.09.2026). Koşul sunucudan
              (`katilimciDuzenleyebilir` = taslak ∧ talep eden) — ekran ikinci
              kural yazmaz; düğme yoksa uç da 403/409 verir. */}
          <IzinKunyesi detay={detay.data} />
          {/* ✏️ K-98 (bulgu 98): AKTİF İZNE KATILIMCI — dar yol. Kart izin
              aktifken (ekleme kapısı dolu) ya da karar bekleyen satır varken
              çıkar; bayraklar ve sebepler sunucunun kapılarından. */}
          {(detay.data.yetkiler.katilimciEklemeKapisi !== null ||
            detay.data.katilimcilar.some((k) => k.onayDurumu === 'bekliyor')) && (
            <AktifKatilimciKarti detay={detay.data} />
          )}

          <Card withBorder>
            <QueryStatus queries={[uygunluk]} />
            {uygunluk.isSuccess && (
              <UygunlukListesi sonuc={uygunluk.data} baslik="Anlık uygunluk kontrolü" />
            )}
          </Card>

          <UzatmaTalebiKarti detay={detay.data} />

          {detay.data.acikIhlaller.length > 0 && (
            <IhlalKararKarti permitId={detay.data.permit.id} ihlaller={detay.data.acikIhlaller} />
          )}

          {/* ✏️ S8-1 (F4·S8) — TÜRE ÖZEL FORM ALANLARI artık BURADA dolar:
              taslak izinde (revizyona dönüş dahil) form gömülü düzenlenir;
              gönderilmiş form kendi sürümüyle salt okunur. Formlar modülüne
              yönlendirme KALKTI (S8-4). Yetki sunucudan (S8-2). */}
          <IzinTuruFormBolumu
            permitId={detay.data.permit.id}
            workplaceId={detay.data.permit.workplaceId}
            izinTaslak={detay.data.permit.durum === 'draft'}
            doldurabilir={detay.data.yetkiler.turFormuDoldurabilir}
          />

          {/* İTA — İŞ TEHLİKE ANALİZİ (tur 11 / R6; Kapsam §16.7.4/6).
              ⚠️ Kart HER ZAMAN çizilir: düzenlenemez durumda SALT OKUNUR
              olur (§2.40 sıralaması — girilmiş bir analizi gizlemek,
              onaylayana "yapılmamış" izlenimi verirdi).
              ✏️ GEZİNTİ ARTÇISI 3 / B6 (ürün sahibi 08.09.2026): kart TÜR
              FORMUNDAN SONRA durur — akış genelden özele (künye → katılımcı →
              ekipman → tür formu → İTA → gönder); tür formundan önce konursa
              kullanıcı henüz neyi analiz edeceğini bilmiyor. Açılma davranışı
              (taslak sonrası) DEĞİŞMEDİ. Sıra e2e ile ölçülür (T5). */}
          <ItaKarti detay={detay.data} />
          <OnaylarBolumu detay={detay.data} yenileniyor={detay.isFetching} />
          {/* ✏️ 28.09.2026 (K-B1): İŞ ÖNCESİ BRİFİNG — iznin kendi kaydı,
              onaylardan SONRA durur (brifing açılış onayından sonra girilir).
              Bölüm kendi ucundan okur; izin detayı yanıtı brifing TAŞIMAZ. */}
          <BrifingBolumu permitId={detay.data.permit.id} />
          <GecisGecmisi detay={detay.data} />
          <IzolasyonKarti detay={detay.data} />
          <GeriCekilmeGecmisi detay={detay.data} />
          {/* ✏️ DİLİM 2 (K-73f): İZ SATIRI — tek satır, gri, EN ALTTA. İzin
              verildiği andaki kapalı aileler (donmuşsa kayıttan); metin
              sunucudan, PDF ile AYNI cümle. Kapalı aile yoksa satır yok. */}
          {detay.data.kapaliKontroller.metin && (
            <Text
              size="sm"
              c="dimmed"
              data-testid="izin-kapali-kontroller"
              data-donduruldu={detay.data.kapaliKontroller.donduruldu}
            >
              {detay.data.kapaliKontroller.metin}
            </Text>
          )}
        </>
      )}
    </Stack>
  );
}

/**
 * İZİN KÜNYESİ — ✏️ **K7.5 (tur 10): P9 IZGARASI.**
 *
 * Künye altı satırlık iki kolonlu bir TABLOYDU: her alan tam genişlikte bir
 * satır kaplıyor, geçerlilik ile izin türünü birlikte görmek için sayfa
 * kaydırılıyordu. **P9 (§20.3b):** kısa alanlar yan yana IZGARADA, uzun
 * (liste gibi) alanlar TAM GENİŞLİKTE altta.
 *
 * ⚠️ **KATILIMCI VE EKİPMAN IZGARAYA GİRMEZ** ve bu, P9'un asıl noktasıdır:
 * ikisi de LİSTEDİR ve üçte bir genişlikte bir hücrede kırpılır (tur 4½ / B1
 * — *görünüyor · çalışıyor · KULLANILAMIYOR* sınıfı tam olarak böyle doğdu).
 *
 * ⚠️ **KATILIMCI = (KİŞİ, GÖREV) ÇİFTİDİR** (Kapsam §19/11-3): görev rozeti
 * süs değil, yetkinlik şartının asıldığı eksendir — kırpılamaz.
 */
function IzinKunyesi({ detay }: { detay: PtwPermitDetail }): React.JSX.Element {
  // ✏️ K-T1: taslak ∧ talep eden → künye DÜZENLENİR. Bayrak katılımcı · sorumlu
  // · İTA ile AYNI yüklemden (`ptwTaslakIcerikKilidi`) — ikinci koşul yazılmaz.
  if (detay.yetkiler.katilimciDuzenleyebilir) {
    return (
      <Bolum baslik="Künye" testId="izin-kunye">
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <KunyeAlani etiket="Talep eden" deger={detay.permit.talepEdenAd} />
            <SorumluSecici detay={detay} />
          </SimpleGrid>
          <KunyeDuzenleyici detay={detay} />
          <Stack gap={0} data-testid="izin-kunye-katilimcilar">
            <KatilimciKarti detay={detay} />
          </Stack>
        </Stack>
      </Bolum>
    );
  }
  return (
    <Bolum baslik="Künye" testId="izin-kunye">
      <Stack gap="sm">
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
          <KunyeAlani etiket="Talep eden" deger={detay.permit.talepEdenAd} />
          {/* K-95c (bulgu 95): taslakta talep eden sorumluyu künyenin
              DÜZENLEME kolundan seçer (yukarıdaki dal — aynı yüklem). */}
          <KunyeAlani
            etiket="Güncel sorumlu"
            deger={detay.guncelSorumluAd ?? '—'}
            testId="izin-kunye-sorumlu"
          />
          <KunyeAlani
            etiket="Geçerlilik"
            deger={<GecerlilikHucresi permit={detay.permit} />}
            testId="izin-kunye-gecerlilik"
          />
        </SimpleGrid>

        <KunyeAlani
          etiket="İzin türleri"
          testId="izin-kunye-turler"
          deger={
            detay.izinTurleri.length === 0 ? (
              '—'
            ) : (
              <Group gap="xs">
                {detay.izinTurleri.map((t) => (
                  <Badge key={t.refId} variant="light">
                    {t.ad}
                  </Badge>
                ))}
              </Group>
            )
          }
        />

        <KunyeAlani
          etiket="Katılımcılar"
          testId="izin-kunye-katilimcilar"
          deger={
            detay.katilimcilar.length === 0 ? (
              '—'
            ) : (
              <Group gap="sm">
                {detay.katilimcilar.map((k) => (
                  <Group key={`${k.personId}-${k.gorevRefId}`} gap={6} wrap="nowrap">
                    {/* Text `<p>` üretir; Badge `<div>`dir — iç içe
                        geçmemeleri için satır Group ile kurulur. */}
                    <Text size="sm">{k.personAd}</Text>
                    <Badge variant="light">{k.gorevAd}</Badge>
                    {/* K-98: kararı bekleyen kişi iznin kapsamında değildir. */}
                    {k.onayDurumu === 'bekliyor' && <Rozet anlam="dikkat">Onay bekliyor</Rozet>}
                  </Group>
                ))}
              </Group>
            )
          }
        />

        <KunyeAlani
          etiket="İş ekipmanları"
          testId="izin-kunye-ekipman"
          deger={detay.ekipmanlar.map((e) => e.ad).join(', ') || '—'}
        />
      </Stack>
    </Bolum>
  );
}

/** P9 alanı — etiket üstte küçük, değer altta (aksiyon/eğitim künyesiyle
 *  aynı biçim; ekran kendi ölçüsünü yazmaz — §2.38). */
function KunyeAlani({
  etiket,
  deger,
  testId,
}: {
  etiket: string;
  deger: React.ReactNode;
  testId?: string;
}): React.JSX.Element {
  return (
    <Stack gap={2} data-testid={testId}>
      <Text size="xs" c="dimmed">
        {etiket}
      </Text>
      {typeof deger === 'string' ? <Text size="sm">{deger}</Text> : deger}
    </Stack>
  );
}

/**
 * KATILIMCI KARTI (P-1) — TASLAKTA katılımcı ekle/çıkar.
 *
 * ⚠️ KATILIMCI = (KİŞİ, GÖREV) ÇİFTİDİR (Kapsam §19/11-3): kişi tek başına
 * eklenemez, çünkü yetkinlik şartı izne değil izin içi GÖREVE asılıdır —
 * görevsiz katılımcının hangi belgesinin aranacağı bilinemez. Kural talep
 * formundakiyle aynıdır; havuzlar da AYNI uçtan (`ptw/permits/options`)
 * gelir, ikinci havuz kurulmaz.
 *
 * Yazma TAM LİSTE gönderir (`setPtwParticipants` sözleşmesi) — kısmi
 * ekleme/çıkarma ucu yoktur.
 */
/**
 * GÜNCEL SORUMLU SEÇİCİSİ (K-95c — bulgu 95). Aday listesi iznin işyerinden
 * gelir (sunucu süzgeci; *tüm holding* kutusu destek personeli için açık —
 * tur 10 / K2). Seçim anında yazılır: ayrı kaydet düğmesi yok, hata yanında.
 */
function SorumluSecici({ detay }: { detay: PtwPermitDetail }): React.JSX.Element {
  const [tumHolding, setTumHolding] = useState(false);
  const secenekler = useIzinSecenekleri(detay.permit.workplaceId, tumHolding);
  const yaz = useIzinSorumlusu();
  return (
    <Stack gap={4}>
      <Select
        label="Güncel sorumlu"
        searchable
        data-testid="izin-sorumlu-sec"
        data={(secenekler.data?.sorumluAdaylari ?? []).map((s) => ({
          value: s.employmentId,
          label: `${kisiEtiketi(s.personAd, s.sahaGirisiGecerli)} — ${s.isverenAd}`,
        }))}
        value={detay.guncelSorumluEmploymentId}
        placeholder={detay.guncelSorumluAd ?? 'Seçilmedi'}
        disabled={yaz.isPending}
        onChange={(deger) => {
          if (deger && deger !== detay.guncelSorumluEmploymentId) {
            yaz.mutate({ permitId: detay.permit.id, guncelSorumluEmploymentId: deger });
          }
        }}
      />
      <Checkbox
        size="xs"
        label="Tüm holdingdeki kişileri göster"
        checked={tumHolding}
        onChange={(olay) => setTumHolding(olay.currentTarget.checked)}
        data-testid="izin-sorumlu-tum-holding"
      />
      {yaz.isError && (
        <Text size="sm" c={ANLAM.engel} role="alert" data-testid="izin-sorumlu-hata">
          {yaz.error.message}
        </Text>
      )}
    </Stack>
  );
}

function KatilimciKarti({ detay }: { detay: PtwPermitDetail }): React.JSX.Element {
  // ✏️ TUR 10 / K2: kişi havuzu iznin işyeriyle sınırlı; kutu, başka
  // işyerinden çağrılan destek personelini eklemeyi açık bırakır.
  const [tumHolding, setTumHolding] = useState(false);
  const secenekler = useIzinSecenekleri(detay.permit.workplaceId, tumHolding);
  const yaz = useIzinKatilimcilari();
  const [yeniKisi, setYeniKisi] = useState<string | null>(null);
  const [yeniGorev, setYeniGorev] = useState<string | null>(null);

  const mevcut = detay.katilimcilar.map((k) => ({
    personId: k.personId,
    gorevRefId: k.gorevRefId,
  }));

  return (
    <Card withBorder data-testid="izin-katilimci-karti">
      <Stack gap="xs">
        <Text fw={600}>Katılımcılar</Text>
        <Text size="xs" c="dimmed">
          Katılımcı kişi + GÖREV çiftidir; yetkinlik şartı göreve asılıdır. Katılımcısız talep onaya
          gönderilemez — uygunluk kontrolü dolaşacak kayıt bulamaz.
        </Text>
        {detay.katilimcilar.length > 0 && (
          <Table data-testid="izin-katilimci-listesi">
            <Table.Tbody>
              {detay.katilimcilar.map((k) => (
                <Table.Tr key={`${k.personId}-${k.gorevRefId}`}>
                  <Table.Td>{k.personAd}</Table.Td>
                  <Table.Td>
                    <Badge variant="light">{k.gorevAd}</Badge>
                  </Table.Td>
                  <Table.Td w={120}>
                    <Button
                      size="xs"
                      variant="subtle"
                      color={ANLAM.engel}
                      data-testid="izin-katilimci-sil"
                      loading={yaz.isPending}
                      onClick={() =>
                        yaz.mutate({
                          permitId: detay.permit.id,
                          katilimcilar: mevcut.filter(
                            (satir) =>
                              !(satir.personId === k.personId && satir.gorevRefId === k.gorevRefId),
                          ),
                        })
                      }
                    >
                      Kaldır
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
        <Group align="end" wrap="wrap">
          <Select
            label="Kişi"
            searchable
            data-testid="izin-katilimci-kisi"
            data={(secenekler.data?.katilimciAdaylari ?? []).map((aday) => ({
              value: aday.personId,
              // K2.2: yüklenici işçisi saha girişi rozetiyle görünür.
              label: kisiEtiketi(aday.ad, aday.sahaGirisiGecerli),
            }))}
            value={yeniKisi}
            onChange={setYeniKisi}
          />
          <Checkbox
            size="sm"
            label="Tüm holdingdeki kişileri göster"
            checked={tumHolding}
            onChange={(olay) => setTumHolding(olay.currentTarget.checked)}
            data-testid="izin-katilimci-tum-holding"
          />
          <Select
            label="Görev"
            data-testid="izin-katilimci-gorev"
            data={(secenekler.data?.gorevler ?? []).map((gorev) => ({
              value: gorev.id,
              label: gorev.ad,
            }))}
            value={yeniGorev}
            onChange={setYeniGorev}
          />
          <Button
            data-testid="izin-katilimci-ekle"
            disabled={!yeniKisi || !yeniGorev}
            loading={yaz.isPending}
            onClick={() => {
              if (!yeniKisi || !yeniGorev) return;
              const varMi = mevcut.some(
                (satir) => satir.personId === yeniKisi && satir.gorevRefId === yeniGorev,
              );
              if (varMi) return;
              yaz.mutate(
                {
                  permitId: detay.permit.id,
                  katilimcilar: [...mevcut, { personId: yeniKisi, gorevRefId: yeniGorev }],
                },
                {
                  onSuccess: () => {
                    setYeniKisi(null);
                    setYeniGorev(null);
                  },
                },
              );
            }}
          >
            Ekle
          </Button>
        </Group>
        {yaz.isError && (
          <Text size="sm" c={ANLAM.engel} role="alert" data-testid="izin-katilimci-hata">
            {yaz.error.message}
          </Text>
        )}
      </Stack>
    </Card>
  );
}

/**
 * ✏️ K-98 (bulgu 98) — AKTİF İZNE KATILIMCI EKLEME + KİŞİ BAZINDA KARAR.
 *
 * İş DURMAZ, yeni kişi BEKLER: güncel sorumlu kişi + görev ekler (uygunluk
 * sunucuda ekleme anında koşar — engelleyen kalem varsa hata metni kalemleri
 * adıyla söyler), satır *"Onay bekliyor"* rozetiyle durur ve iznin kapsamında
 * değildir; izin veren basamağının havuzu satırda onaylar ya da gerekçeyle
 * reddeder. Taslak kartı (`KatilimciKarti`) AYRI kalır — iki yol, iki kapı.
 *
 * ⚠️ Ekran kural bilmez: ekleme satırı `katilimciEklemeKapisi`, karar
 * düğmeleri satırın `katilimciKararKapisi` bayrağından; yazamayan sebebi görür.
 */
function AktifKatilimciKarti({ detay }: { detay: PtwPermitDetail }): React.JSX.Element {
  const [tumHolding, setTumHolding] = useState(false);
  const katilimciEklemeKapisi = detay.yetkiler.katilimciEklemeKapisi;
  const secenekler = useIzinSecenekleri(
    katilimciEklemeKapisi?.yazabilir ? detay.permit.workplaceId : null,
    tumHolding,
  );
  const ekle = useAktifKatilimciEkle();
  const karar = useKatilimciKarari();
  const [yeniKisi, setYeniKisi] = useState<string | null>(null);
  const [yeniGorev, setYeniGorev] = useState<string | null>(null);
  const [retSatiri, setRetSatiri] = useState<string | null>(null);
  const [gerekce, setGerekce] = useState('');

  return (
    <Card withBorder data-testid="izin-aktif-katilimci-karti">
      <Stack gap="xs">
        <Text fw={600}>Ekibe katılım</Text>
        <Table data-testid="izin-aktif-katilimci-listesi">
          <Table.Tbody>
            {detay.katilimcilar.map((k) => (
              <Table.Tr
                key={k.participantId}
                data-testid="izin-aktif-katilimci-satiri"
                data-durum={k.onayDurumu}
              >
                <Table.Td>{k.personAd}</Table.Td>
                <Table.Td>
                  <Badge variant="light">{k.gorevAd}</Badge>
                </Table.Td>
                <Table.Td>
                  {k.onayDurumu === 'bekliyor' ? (
                    <Rozet anlam="dikkat" testId="izin-katilimci-onay-bekliyor">
                      Onay bekliyor
                    </Rozet>
                  ) : null}
                </Table.Td>
                <Table.Td>
                  {k.katilimciKararKapisi?.yazabilir ? (
                    retSatiri === k.participantId ? (
                      <Group gap="xs" align="end">
                        <TextInput
                          size="xs"
                          label="Ret gerekçesi"
                          value={gerekce}
                          onChange={(e) => setGerekce(e.currentTarget.value)}
                          data-testid="izin-katilimci-ret-gerekce"
                        />
                        <Button
                          size="xs"
                          color={ANLAM.engel}
                          disabled={gerekce.trim() === ''}
                          loading={karar.isPending}
                          data-testid="izin-katilimci-ret-onayla"
                          onClick={() =>
                            karar.mutate(
                              {
                                permitId: detay.permit.id,
                                participantId: k.participantId,
                                sonuc: 'reddedildi',
                                gerekce: gerekce.trim(),
                              },
                              {
                                onSuccess: () => {
                                  setRetSatiri(null);
                                  setGerekce('');
                                },
                              },
                            )
                          }
                        >
                          Reddet
                        </Button>
                        <Button size="xs" variant="default" onClick={() => setRetSatiri(null)}>
                          Vazgeç
                        </Button>
                      </Group>
                    ) : (
                      <Group gap="xs">
                        <Button
                          size="xs"
                          loading={karar.isPending}
                          data-testid="izin-katilimci-onayla"
                          onClick={() =>
                            karar.mutate({
                              permitId: detay.permit.id,
                              participantId: k.participantId,
                              sonuc: 'onaylandi',
                            })
                          }
                        >
                          Onayla
                        </Button>
                        <Button
                          size="xs"
                          variant="light"
                          color={ANLAM.engel}
                          data-testid="izin-katilimci-reddet"
                          onClick={() => {
                            setRetSatiri(k.participantId);
                            setGerekce('');
                          }}
                        >
                          Reddet
                        </Button>
                      </Group>
                    )
                  ) : k.katilimciKararKapisi ? (
                    <KapiSebebi
                      durum={k.katilimciKararKapisi}
                      nesne="Katılımcı kararını"
                      fiil="verir"
                      testId="izin-katilimci-karar-sebep"
                    />
                  ) : null}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        {katilimciEklemeKapisi?.yazabilir ? (
          <Group align="end" wrap="wrap">
            <Select
              label="Kişi"
              searchable
              data-testid="izin-aktif-katilimci-kisi"
              data={(secenekler.data?.katilimciAdaylari ?? []).map((aday) => ({
                value: aday.personId,
                label: kisiEtiketi(aday.ad, aday.sahaGirisiGecerli),
              }))}
              value={yeniKisi}
              onChange={setYeniKisi}
            />
            <Checkbox
              size="sm"
              label="Tüm holdingdeki kişileri göster"
              checked={tumHolding}
              onChange={(olay) => setTumHolding(olay.currentTarget.checked)}
            />
            <Select
              label="Görev"
              data-testid="izin-aktif-katilimci-gorev"
              data={(secenekler.data?.gorevler ?? []).map((gorev) => ({
                value: gorev.id,
                label: gorev.ad,
              }))}
              value={yeniGorev}
              onChange={setYeniGorev}
            />
            <Button
              data-testid="izin-aktif-katilimci-ekle"
              disabled={!yeniKisi || !yeniGorev}
              loading={ekle.isPending}
              onClick={() => {
                if (!yeniKisi || !yeniGorev) return;
                ekle.mutate(
                  { permitId: detay.permit.id, personId: yeniKisi, gorevRefId: yeniGorev },
                  {
                    onSuccess: () => {
                      setYeniKisi(null);
                      setYeniGorev(null);
                    },
                  },
                );
              }}
            >
              Ekibe ekle
            </Button>
          </Group>
        ) : katilimciEklemeKapisi ? (
          <KapiSebebi
            durum={katilimciEklemeKapisi}
            nesne="Aktif izne katılımcıyı"
            fiil="ekler"
            testId="izin-aktif-katilimci-sebep"
          />
        ) : null}
        {(ekle.isError || karar.isError) && (
          <Text size="sm" c={ANLAM.engel} role="alert" data-testid="izin-aktif-katilimci-hata">
            {(ekle.error ?? karar.error)?.message}
          </Text>
        )}
      </Stack>
    </Card>
  );
}

/**
 * AKSİYON DÜĞMELERİ. Her biri `yetkiler`den okunur; gerekçe/sözlük isteyen
 * ikisi (geri çekilme · iptal) modal açar, kalanı tek tuştur.
 *
 * GERİ ÇEKİLME TEK TUŞ + GEREKÇEDİR (K2 tasarım gereksinimi): "durdurursam
 * kırtasiye çıkar" tereddüdü yaratmayacak kadar hafif olmalı. Ek adım,
 * onay, form EKLEME.
 */
function IslemDugmeleri({ detay }: { detay: PtwPermitDetail }): React.JSX.Element {
  const islem = useIzinIslemi();
  const pdf = useCiktiIndir();
  const [geriCekme, setGeriCekme] = useState(false);
  const [iptal, setIptal] = useState(false);
  const [uzatma, setUzatma] = useState(false);
  const yetkiler = detay.yetkiler;
  const permitId = detay.permit.id;

  const tekTus = (
    ad: string,
    kod: PtwIzinIslemi,
    testId: string,
    renk?: string,
  ): React.JSX.Element => (
    <Button
      size="xs"
      color={renk}
      variant={renk ? 'light' : 'filled'}
      data-testid={testId}
      loading={islem.isPending}
      onClick={() => islem.mutate({ permitId, islem: kod })}
    >
      {ad}
    </Button>
  );

  return (
    <Stack gap="xs" align="end">
      <Group gap="xs">
        {/* ⚠️ **K4 (tur 10) — PDF HER DURUMDA VE HERKESE AÇIKTIR.** İzin
            sahada BASILIP ASILAN bir belgedir: taslakken de (ekip ne
            dolduracağını kâğıttan okur), kapandıktan sonra da (denetim
            dosyası) istenir. Çıktı bir OKUMA hakkıdır — bu sayfayı görebilen
            çıktısını da alır (izleyici dahil, §2.36); yeni yetki ekseni
            açılmaz ve `yetkiler`e bakılmaz. */}
        <Button
          size="xs"
          variant="default"
          data-testid="izin-pdf"
          loading={pdf.isPending}
          onClick={() =>
            pdf.mutate({ yol: `/api/ptw/permits/${permitId}/pdf`, yedekAd: 'is-izni.pdf' })
          }
        >
          İzin formu (PDF)
        </Button>
        {yetkiler.gonderebilir && tekTus('Onaya gönder', 'submit', 'izin-gonder')}
        {yetkiler.iptalEdebilir && (
          <Button
            size="xs"
            variant="default"
            data-testid="izin-iptal"
            onClick={() => setIptal(true)}
          >
            Talebi iptal et
          </Button>
        )}
        {yetkiler.aktifeGecirebilir && tekTus('Aktife geçir', 'activate', 'izin-aktif')}
        {yetkiler.uzatmaBaslatabilir && (
          <Button
            size="xs"
            variant="default"
            data-testid="izin-uzatma"
            onClick={() => setUzatma(true)}
          >
            Uzatma iste
          </Button>
        )}
        {yetkiler.isimBittiBildirebilir && tekTus('İşim bitti', 'work-completed', 'izin-is-bitti')}
        {yetkiler.sahaKontrolYapabilir &&
          tekTus('Saha kontrolü yapıldı', 'field-check', 'izin-saha-kontrol')}
        {/* Yetkisiz kullanıcı bu düğmeyi HİÇ görmez (K2) — görünürlük de,
            ret de aynı yüklemden gelir. */}
        {yetkiler.geriCekebilir && (
          <Button
            size="xs"
            color={ANLAM.engel}
            data-testid="izin-geri-cek"
            onClick={() => setGeriCekme(true)}
          >
            İzni geri çek
          </Button>
        )}
      </Group>
      {/* ✏️ BULGU 95 (P13): işbaşı bekleyen izinde düğmeyi göremeyen kullanıcı
          NEDENİNİ görür — sorumlusuz izinde düğme herkese sebepsiz gizleniyordu.
          Sebep sunucunun reddettiği cümlenin aynısıdır. */}
      {yetkiler.aktifeGecisKapisi && (
        <KapiSebebi
          durum={yetkiler.aktifeGecisKapisi}
          nesne="Bu izni"
          fiil="aktife geçirir"
          testId="izin-aktif-sebep"
        />
      )}
      {islem.isError && (
        <Text size="sm" c={ANLAM.engel} role="alert" data-testid="izin-islem-hatasi">
          {islem.error.message}
        </Text>
      )}
      <GeriCekmeKutusu
        permitId={permitId}
        acik={geriCekme}
        workplaceId={detay.permit.workplaceId}
        onClose={() => setGeriCekme(false)}
      />
      <IptalKutusu permitId={permitId} acik={iptal} onClose={() => setIptal(false)} />
      <UzatmaKutusu
        permitId={permitId}
        mevcutBitis={detay.permit.gecerlilikBitis}
        acik={uzatma}
        onClose={() => setUzatma(false)}
      />
    </Stack>
  );
}

/**
 * GERİ ÇEKME KUTUSU — sözlükten gerekçe (8 kalem + "Diğer"). "Diğer"de
 * serbest metin ZORUNLUDUR; kuralın kendisi SERVİSTEDİR (cevabı başka
 * tablonun satırında olduğu için DB CHECK yazılamaz) — ekran yalnız yıldızı
 * gösterir ve sunucunun 422'sini okur.
 */
function GeriCekmeKutusu({
  permitId,
  workplaceId,
  acik,
  onClose,
}: {
  permitId: string;
  workplaceId: string;
  acik: boolean;
  onClose: () => void;
}): React.JSX.Element {
  const secenekler = useIzinSecenekleri(acik ? workplaceId : null);
  const islem = useIzinIslemi();
  const [gerekceRefId, setGerekceRefId] = useState<string | null>(null);
  const [metin, setMetin] = useState('');
  const gerekceler = secenekler.data?.geriCekilmeGerekceleri ?? [];
  const digerMi = gerekceler.find((g) => g.id === gerekceRefId)?.digerMi ?? false;

  return (
    <Modal opened={acik} onClose={onClose} title="İzni geri çek">
      <Stack>
        <Text size="sm" c="dimmed">
          Geri çekilme işi durdurur ve izni KAPANIŞ sürecine zorlar; iptal değildir (başlamış iş
          iptal edilemez). Tek adımdır: gerekçeyi seçin, kayıt ve durum geçişi birlikte yazılır.
        </Text>
        <Select
          label="Gerekçe"
          withAsterisk
          data={gerekceler.map((g) => ({ value: g.id, label: g.ad }))}
          value={gerekceRefId}
          onChange={setGerekceRefId}
          data-testid="geri-cekme-gerekce"
          searchable
        />
        <Textarea
          label="Açıklama"
          withAsterisk={digerMi}
          description={
            digerMi
              ? '"Diğer" gerekçesinde açıklama zorunludur — raporda ayrı sayılır.'
              : 'İsteğe bağlı; kayda geçer.'
          }
          value={metin}
          onChange={(e) => setMetin(e.currentTarget.value)}
          data-testid="geri-cekme-metin"
        />
        {islem.isError && (
          <Text size="sm" c={ANLAM.engel} role="alert">
            {islem.error.message}
          </Text>
        )}
        <Button
          color={ANLAM.engel}
          data-testid="geri-cekme-onayla"
          disabled={gerekceRefId === null || (digerMi && metin.trim() === '')}
          loading={islem.isPending}
          onClick={() =>
            islem.mutate(
              {
                permitId,
                islem: 'withdraw',
                govde: {
                  gerekceRefId: gerekceRefId!,
                  gerekceMetin: metin.trim() === '' ? null : metin.trim(),
                },
              },
              { onSuccess: onClose },
            )
          }
        >
          Geri çek
        </Button>
      </Stack>
    </Modal>
  );
}

/** İptal — YALNIZ talep eden ve YALNIZ aktife hiç geçmemiş talepte (K13). */
function IptalKutusu({
  permitId,
  acik,
  onClose,
}: {
  permitId: string;
  acik: boolean;
  onClose: () => void;
}): React.JSX.Element {
  const islem = useIzinIslemi();
  const [gerekce, setGerekce] = useState('');
  return (
    <Modal opened={acik} onClose={onClose} title="Talebi iptal et">
      <Stack>
        <Text size="sm" c="dimmed">
          İptal, talebi açanın geri adımıdır ve yalnız iş başlamadan mümkündür. Onaycının reddiyle
          karıştırılmaz — raporda ayrı sayılırlar.
        </Text>
        <Textarea
          label="Gerekçe"
          description="İsteğe bağlı; kayda geçer."
          value={gerekce}
          onChange={(e) => setGerekce(e.currentTarget.value)}
          data-testid="iptal-gerekce"
        />
        {islem.isError && (
          <Text size="sm" c={ANLAM.engel} role="alert">
            {islem.error.message}
          </Text>
        )}
        <Button
          variant="default"
          data-testid="iptal-onayla"
          loading={islem.isPending}
          onClick={() =>
            islem.mutate(
              {
                permitId,
                islem: 'cancel',
                govde: { gerekce: gerekce.trim() === '' ? null : gerekce.trim() },
              },
              { onSuccess: onClose },
            )
          }
        >
          İptal et
        </Button>
      </Stack>
    </Modal>
  );
}

/**
 * UZATMA TALEBİ (madde 52) — uzatma bir durum değil KAYITTIR: YENİ BİTİŞ +
 * GEREKÇE zorunludur. Zincirin bekleyen listesi bu kayıttan beslenir;
 * istenmemiş uzatma onaycıya HİÇ görünmez.
 *
 * Yeni bitişin mevcut bitişten sonra olması kuralı SUNUCUDADIR (422); ekran
 * yalnız alanın alt sınırını ipucu olarak gösterir — ikinci kural yazmaz.
 */
function UzatmaKutusu({
  permitId,
  mevcutBitis,
  acik,
  onClose,
}: {
  permitId: string;
  mevcutBitis: string;
  acik: boolean;
  onClose: () => void;
}): React.JSX.Element {
  const islem = useIzinIslemi();
  const [yeniBitis, setYeniBitis] = useState('');
  const [gerekce, setGerekce] = useState('');

  return (
    <Modal opened={acik} onClose={onClose} title="Uzatma iste">
      <Stack>
        <Text size="sm" c="dimmed">
          Uzatma bir durum değil, kayıttır: yeni bitiş ve gerekçe zincire gider, aynı zincir karar
          verir. Onaylanırsa geçerlilik buradaki tarihe uzar. Mevcut bitiş:{' '}
          {zamanGoster(mevcutBitis)}
        </Text>
        <TextInput
          type="datetime-local"
          label="Yeni bitiş"
          withAsterisk
          value={yeniBitis}
          onChange={(e) => setYeniBitis(e.currentTarget.value)}
          data-testid="uzatma-yeni-bitis"
        />
        <Textarea
          label="Gerekçe"
          withAsterisk
          description="Zorunlu: sürekli uzatma denetimi kayıtsız yapılamaz."
          value={gerekce}
          onChange={(e) => setGerekce(e.currentTarget.value)}
          data-testid="uzatma-gerekce"
        />
        {islem.isError && (
          <Text size="sm" c={ANLAM.engel} role="alert" data-testid="uzatma-hatasi">
            {islem.error.message}
          </Text>
        )}
        <Button
          data-testid="uzatma-onayla"
          disabled={yeniBitis === '' || gerekce.trim() === ''}
          loading={islem.isPending}
          onClick={() =>
            islem.mutate(
              {
                permitId,
                islem: 'extension-request',
                govde: {
                  yeniBitis: new Date(yeniBitis).toISOString(),
                  gerekce: gerekce.trim(),
                },
              },
              { onSuccess: onClose },
            )
          }
        >
          Uzatma talebini gönder
        </Button>
      </Stack>
    </Modal>
  );
}

/** AÇIK UZATMA TALEBİ — kaydın kendisi kapıdır; onay yüzü de bunu okur. */
function UzatmaTalebiKarti({ detay }: { detay: PtwPermitDetail }): React.JSX.Element | null {
  const talep = detay.acikUzatmaTalebi;
  if (!talep) return null;
  return (
    <Card withBorder data-testid="izin-uzatma-talebi">
      <Stack gap="xs">
        <Group gap="xs">
          <Text fw={600}>Uzatma talebi — karar bekliyor</Text>
          <Badge variant="light">{talep.turNo}. uzatma turu</Badge>
        </Group>
        <KunyeTablosu
          satirlar={[
            { etiket: 'İsteyen', deger: talep.talepEdenAd },
            { etiket: 'Yeni bitiş', deger: zamanGoster(talep.yeniBitis) },
            { etiket: 'Gerekçe', deger: talep.gerekce },
            { etiket: 'İstenme zamanı', deger: zamanGoster(talep.zaman) },
          ]}
        />
      </Stack>
    </Card>
  );
}

/**
 * K7 İHLAL KARARI — damgalı izinde İZİN VERENE açılır. Liste sunucuda
 * kişiselleştirilmiştir (başkasında boş döner), bu yüzden burada ikinci bir
 * yetki kontrolü yapılmaz.
 *
 * İki sonuç: SÜRDÜR (gerekçe zorunlu — kayıtlıdır, raporda görünür) ·
 * GERİ ÇEK (K2 yolu). Geri çekmede gerekçe kodu K9 ile OTOMATİK önerilir ve
 * izin veren üstüne yazabilir; öneri sunucudan gelir.
 */
function IhlalKararKarti({
  permitId,
  ihlaller,
}: {
  permitId: string;
  ihlaller: PtwViolationSummary[];
}): React.JSX.Element {
  const karar = useIhlalKarari();
  const [acik, setAcik] = useState<{
    ihlal: PtwViolationSummary;
    sonuc: 'surdur' | 'geri_cek';
  } | null>(null);
  const [gerekce, setGerekce] = useState('');

  return (
    <Card withBorder data-testid="ihlal-karar-karti">
      <Stack>
        <Alert color={ANLAM.engel} title="Uygunluk ihlali — kararınız bekleniyor" role="alert">
          <Text size="sm">
            Kaynak otomatik düşmez, izin otomatik durmaz: kararı izin veren verir. Sürdürme
            gerekçesi kayıtlıdır ve raporda görünür.
          </Text>
        </Alert>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Kalem</Table.Th>
              <Table.Th>Ne oldu</Table.Th>
              <Table.Th>Karar</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {ihlaller.map((ihlal) => (
              <Table.Tr key={ihlal.id} data-testid="ihlal-satiri">
                <Table.Td>
                  <Text size="sm" fw={600}>
                    {ihlal.kalem}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {zamanGoster(ihlal.bildirimAt)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{ihlal.mesaj}</Text>
                  <Text size="xs" c="dimmed">
                    {ihlal.neYapilmali}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <Button
                      size="xs"
                      variant="default"
                      data-testid="ihlal-surdur"
                      onClick={() => setAcik({ ihlal, sonuc: 'surdur' })}
                    >
                      Sürdür
                    </Button>
                    <Button
                      size="xs"
                      color={ANLAM.engel}
                      data-testid="ihlal-geri-cek"
                      onClick={() => setAcik({ ihlal, sonuc: 'geri_cek' })}
                    >
                      Geri çek
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>

      <Modal
        opened={acik !== null}
        onClose={() => setAcik(null)}
        title={acik?.sonuc === 'surdur' ? 'İşi sürdür' : 'İzni geri çek'}
      >
        <Stack>
          <Text size="sm" c="dimmed">
            {acik?.sonuc === 'surdur'
              ? 'İzin sahada kalır. Gerekçe zorunludur ve raporda görünür — sık "sürdür" kararı bulgudur.'
              : 'Geri çekme izni kapanış sürecine zorlar. Gerekçe kodu uygunsuzluk kaleminden otomatik düşer.'}
          </Text>
          {acik?.sonuc === 'surdur' && (
            <Textarea
              label="Gerekçe"
              withAsterisk
              value={gerekce}
              onChange={(e) => setGerekce(e.currentTarget.value)}
              data-testid="ihlal-gerekce"
            />
          )}
          {karar.isError && (
            <Text size="sm" c={ANLAM.engel} role="alert">
              {karar.error.message}
            </Text>
          )}
          <Button
            color={acik?.sonuc === 'geri_cek' ? ANLAM.engel : undefined}
            data-testid="ihlal-karar-onayla"
            disabled={acik?.sonuc === 'surdur' && gerekce.trim() === ''}
            loading={karar.isPending}
            onClick={() => {
              if (!acik) return;
              karar.mutate(
                {
                  violationId: acik.ihlal.id,
                  permitId,
                  input:
                    acik.sonuc === 'surdur'
                      ? { karar: 'surdur', gerekce: gerekce.trim() }
                      : // Gerekçe kodu VERİLMEZSE K9 önerisi sunucuda düşer;
                        // izin veren üstüne yazmak isterse öneri doludur.
                        { karar: 'geri_cek', gerekceRefId: acik.ihlal.onerilenGerekceRefId },
                },
                {
                  onSuccess: () => {
                    setGerekce('');
                    setAcik(null);
                  },
                },
              );
            }}
          >
            Kararı kaydet
          </Button>
        </Stack>
      </Modal>
    </Card>
  );
}

/**
 * ONAYLAR BLOĞU (PTW onay düğmesi, 14.09.2026 — bulgu 65 · 62 · 63; Kapsam
 * §16.7 ✏️ K-62 · K-63 · K-65 · §20.3b P13-d).
 *
 * ⚠️ **AYNI SORUYA İKİ YÜZEY, TEK CEVAP:** havuzdaki onaycı İşlerim'de satırı
 * görüyor, burada *"Henüz onay kararı yok"* okuyordu — düğme de sebep de yoktu.
 * Sunucu tek cevap veriyordu (`yetkiler.onayVerebilir`) ve ekran onu hiç
 * okumuyordu (32 gün). Şimdi blok basamağı gösterir; düğme
 * `kararKapisi.yazabilir`den, yazamayanın sebebi `<KapiSebebi>`den çizilir —
 * ekran ne şapka listesi yazar ne havuz hesaplar (`yazma-kapisi-envanteri`).
 * Karar kutusu İşlerim'inkinin **kendisidir** (`KararKutusu`).
 *
 * ⚠️ *Yükleniyor* ile *bitti* ayrı görünür (C1): karar sonrası detay
 * tazelenirken düğmeler pasif + *"yenileniyor"* — pasif düğme *"bitti"* demek
 * değildir.
 *
 * Verilmiş kararlar (tüm turlar) alttaki kayıt tablosunda kalır; kapısının
 * güncel turundan eski satır **sıfırlandı** damgası taşır (revizyon = yeni tur;
 * eski imzalar SİLİNMEZ — §2.9). Damga sunucudan (`sifirlandi`).
 */
function OnaylarBolumu({
  detay,
  yenileniyor,
}: {
  detay: PtwPermitDetail;
  yenileniyor: boolean;
}): React.JSX.Element {
  const [karar, setKarar] = useState<KararSecimi | null>(null);
  const blok = detay.onayBasamaklari;

  const secim = (basamak: PtwOnayBasamagi, sonuc: KararSecimi['sonuc']): KararSecimi | null => {
    if (!blok || blok.turNo === null) return null;
    return {
      satir: {
        permit: detay.permit,
        chainStepId: basamak.chainStepId,
        stepAd: basamak.stepAd,
        sapka: basamak.sapka,
        izinVeren: basamak.izinVeren,
        kapi: blok.kapi,
        turNo: blok.turNo,
      },
      sonuc,
    };
  };

  return (
    <Card withBorder data-testid="izin-onaylar-bolumu">
      <Stack gap="xs">
        <Group justify="space-between">
          <Text fw={600}>Onaylar</Text>
          {blok && (
            <Text size="xs" c="dimmed" data-testid="izin-onay-kapi">
              {PTW_ONAY_KAPI_ETIKET[blok.kapi]}
              {blok.turNo !== null ? ` · ${blok.turNo}. tur` : ''}
            </Text>
          )}
        </Group>
        {blok === null ? (
          <YazamamaSebebi testId="izin-onay-zincir-yok">
            Bu işyeri için onay zinciri tanımlı değil.
          </YazamamaSebebi>
        ) : (
          <Table data-testid="izin-onay-basamaklari">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Basamak</Table.Th>
                <Table.Th>Durum</Table.Th>
                <Table.Th>Karar</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {blok.basamaklar.map((b) => (
                <Table.Tr
                  key={b.chainStepId}
                  data-testid="izin-onay-basamagi"
                  data-step={b.chainStepId}
                >
                  <Table.Td>
                    <Badge variant={b.izinVeren ? 'filled' : 'light'}>
                      {b.stepAd} · {SAPKA_ETIKET[b.sapka]}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{basamakDurumRozeti(b, blok.acik)}</Table.Td>
                  <Table.Td>
                    {b.karar ? (
                      <>
                        <Text size="sm">{b.karar.onaylayanAd ?? '—'}</Text>
                        <Text size="xs" c="dimmed">
                          {zamanGoster(b.karar.zaman)}
                        </Text>
                        {b.karar.gerekce && <Text size="xs">{b.karar.gerekce}</Text>}
                      </>
                    ) : b.kararKapisi.yazabilir ? (
                      <Group gap="xs">
                        <OnaylaDugmesi
                          permitId={detay.permit.id}
                          testId="izin-onay-onayla"
                          disabled={yenileniyor}
                          onClick={() => setKarar(secim(b, 'approved'))}
                        />
                        <Button
                          size="xs"
                          variant="default"
                          data-testid="izin-onay-revizyon"
                          disabled={yenileniyor}
                          onClick={() => setKarar(secim(b, 'revision_requested'))}
                        >
                          {PTW_ONAY_SONUC_ETIKET.revision_requested}
                        </Button>
                        <Button
                          size="xs"
                          variant="light"
                          color={ANLAM.engel}
                          data-testid="izin-onay-red"
                          disabled={yenileniyor}
                          onClick={() => setKarar(secim(b, 'rejected'))}
                        >
                          {PTW_ONAY_SONUC_ETIKET.rejected}
                        </Button>
                        {yenileniyor && (
                          <Text size="xs" c="dimmed" data-testid="izin-onay-yenileniyor">
                            yenileniyor…
                          </Text>
                        )}
                      </Group>
                    ) : (
                      <KapiSebebi
                        durum={b.kararKapisi}
                        nesne="Bu basamağı"
                        fiil="onaylar"
                        testId={`izin-onay-sebep-${b.sira}`}
                      />
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
        {detay.onaylar.length > 0 && (
          <Table data-testid="izin-onay-listesi">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Kapı / tur</Table.Th>
                <Table.Th>Basamak</Table.Th>
                <Table.Th>Sonuç</Table.Th>
                <Table.Th>Kim</Table.Th>
                <Table.Th>Gerekçe</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {detay.onaylar.map((onay) => (
                <Table.Tr key={onay.id} data-sifirlandi={onay.sifirlandi ? 'evet' : undefined}>
                  <Table.Td>
                    {PTW_ONAY_KAPI_ETIKET[onay.kapi]} · {onay.turNo}. tur
                  </Table.Td>
                  <Table.Td>
                    <Badge variant={onay.izinVeren ? 'filled' : 'light'}>
                      {onay.stepAd} · {SAPKA_ETIKET[onay.sapka]}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <span>{PTW_BASAMAK_DURUM_ETIKET[onay.sonuc]}</span>
                      {onay.sifirlandi && (
                        <Rozet anlam="bilgi" testId="izin-onay-sifirlandi">
                          {PTW_BASAMAK_DURUM_ETIKET.sifirlandi}
                        </Rozet>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{onay.onaylayanAd ?? '—'}</Text>
                    <Text size="xs" c="dimmed">
                      {zamanGoster(onay.zaman)}
                    </Text>
                  </Table.Td>
                  <Table.Td>{onay.gerekce ?? '—'}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
        <KararKutusu karar={karar} onClose={() => setKarar(null)} />
      </Stack>
    </Card>
  );
}

/** Basamağın durumu — ANLAM sözlükten, metin i18n'den; ekran kural hesaplamaz. */
function basamakDurumRozeti(b: PtwOnayBasamagi, acik: boolean): React.JSX.Element {
  if (b.karar) {
    const anlam =
      b.karar.sonuc === 'approved' ? 'olumlu' : b.karar.sonuc === 'rejected' ? 'engel' : 'dikkat';
    return (
      <Rozet anlam={anlam} testId="izin-onay-basamak-durum">
        {PTW_BASAMAK_DURUM_ETIKET[b.karar.sonuc]}
      </Rozet>
    );
  }
  if (acik) {
    return (
      <Rozet anlam="bilgi" testId="izin-onay-basamak-durum">
        {PTW_BASAMAK_DURUM_ETIKET.bekliyor}
      </Rozet>
    );
  }
  return (
    <Text size="sm" c="dimmed" data-testid="izin-onay-basamak-durum">
      —
    </Text>
  );
}

/** DEĞİŞMEZ geçiş log'u — imzanın hukuki geçerliliğinin ikinci şartı. */
function GecisGecmisi({ detay }: { detay: PtwPermitDetail }): React.JSX.Element {
  return (
    <Card withBorder>
      <Stack gap="xs">
        <Text fw={600}>Durum geçmişi</Text>
        <Text size="xs" c="dimmed">
          Her geçiş değişmez yazılır: kim, ne zaman, hangi rolde. Sistem geçişlerinde (süre dolumu)
          kişi ve şapka boştur — sistem yazması bir insana mal edilmez.
        </Text>
        <Table data-testid="izin-gecis-listesi">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>İşlem</Table.Th>
              <Table.Th>Kim</Table.Th>
              <Table.Th>Gerekçe</Table.Th>
              <Table.Th>Zaman</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {detay.gecisler.map((gecis) => (
              <Table.Tr key={gecis.id}>
                <Table.Td>{PTW_ISLEM_ETIKET[gecis.islem] ?? gecis.islem}</Table.Td>
                <Table.Td>
                  {gecis.yapanAd ?? 'Sistem'}
                  {gecis.sapka && (
                    <Text size="xs" c="dimmed">
                      {SAPKA_ETIKET[gecis.sapka]}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>{gecis.gerekce ?? '—'}</Table.Td>
                <Table.Td>
                  <Text size="xs">{zamanGoster(gecis.zaman)}</Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </Card>
  );
}

/** İZOLASYON/LOTO — SALT OKUNUR: saha aksiyonları S5'te. */
function IzolasyonKarti({ detay }: { detay: PtwPermitDetail }): React.JSX.Element {
  const acik = detay.izolasyonlar.filter((iz) => iz.durum !== 'removed');
  return (
    <Card withBorder data-testid="izin-izolasyon-karti">
      <Stack gap="xs">
        <Group gap="xs">
          <Text fw={600}>İzolasyon / LOTO</Text>
          {acik.length > 0 && <Badge color={ANLAM.dikkat}>{acik.length} açık nokta</Badge>}
        </Group>
        {detay.izolasyonlar.length === 0 ? (
          <Text size="sm" c="dimmed">
            İzolasyon kaydı yok. (Kayıt açma ve kilit işlemleri saha yüzeyindedir.)
          </Text>
        ) : (
          <>
            <Text size="xs" c="dimmed">
              Açık izolasyon varken kapanış onaylanamaz — kişisel kilitler çıkmadan izolasyon
              kalkmaz.
            </Text>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Nokta</Table.Th>
                  <Table.Th>Kilit / etiket</Table.Th>
                  <Table.Th>Durum</Table.Th>
                  <Table.Th>Takan</Table.Th>
                  <Table.Th>Açık kilit</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {detay.izolasyonlar.map((iz) => (
                  <Table.Tr key={iz.id}>
                    <Table.Td>{iz.izolasyonNoktasi}</Table.Td>
                    <Table.Td>
                      {[iz.kilitNo, iz.etiketNo].filter(Boolean).join(' / ') || '—'}
                    </Table.Td>
                    <Table.Td>{PTW_IZOLASYON_DURUM_ETIKET[iz.durum]}</Table.Td>
                    <Table.Td>{iz.takanAd}</Table.Td>
                    <Table.Td>{iz.acikKilitSayisi}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </>
        )}
      </Stack>
    </Card>
  );
}

function GeriCekilmeGecmisi({ detay }: { detay: PtwPermitDetail }): React.JSX.Element | null {
  if (detay.geriCekilmeler.length === 0) return null;
  return (
    <Card withBorder>
      <Stack gap="xs">
        <Text fw={600}>Geri çekilme kayıtları</Text>
        <Table data-testid="izin-geri-cekilme-listesi">
          <Table.Tbody>
            {detay.geriCekilmeler.map((gc) => (
              <Table.Tr key={gc.id}>
                <Table.Td>{gc.gerekceAd}</Table.Td>
                <Table.Td>{gc.gerekceMetin ?? '—'}</Table.Td>
                <Table.Td>
                  <Badge variant="light">{PTW_GERI_CEKILME_KAYNAK_ETIKET[gc.kaynak]}</Badge>
                </Table.Td>
                <Table.Td>{gc.durduranAd}</Table.Td>
                <Table.Td>
                  <Text size="xs">{zamanGoster(gc.zaman)}</Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </Card>
  );
}

/**
 * İTA — İŞ TEHLİKE ANALİZİ (tur 11 / R6; Kapsam §16.7.4/6).
 *
 * ⚠️ **SATIR = ADIM → TEHLİKE → ÖNLEM.** Sorumlu kolonu ve skor YOKTUR:
 * sorumluluk iznin kendi zincirindedir, skor risk modülünün metodolojik
 * işidir. İkisi de eklenirse izin içinde ikinci bir takip yolu ve ikinci bir
 * ölçek doğar.
 *
 * ⚠️ **HER İZİNDE SIFIRDAN — KÜTÜPHANEDEN ÖN DOLDURMA YOK.** Ekranda
 * "şablondan getir" düğmesi yoktur ve olmayacaktır: ön dolu bir İTA analizi
 * bir **talimata** çevirir; değeri metninde değil, yazılırken
 * düşünülmesindedir.
 *
 * ⚠️ **ZORUNLULUK YOK:** boş İTA onaya gidebilir ve onaycı reddeder. Ekran
 * bunu **söyler** — kullanıcı doldurmadığı bir alanın gönderimi
 * engellediğini sanmasın.
 *
 * ⚠️ **YETKİ SUNUCUDAN** (`jsaDuzenleyebilir` = taslak ∧ talep eden); ekran
 * ikinci kural yazmaz — düğme yoksa uç da 403/409 verir.
 *
 * ✏️ **GEZİNTİ ARTÇISI 2 / B2.2 (11.09.2026) — İKİ DÜZELTME:**
 * - **SATIR İÇİ EKLEME (P1-a):** giriş eskiden tablonun ALTINDA üç etiketli
 *   kutu + *"Satır ekle"* düğmesiydi; ürün sahibi onu **ekleme satırı olarak
 *   tanımadı** (*"taslak izinde de İTA ekleme satırı yok"*). Artık tablonun
 *   sonunda **soluk satır** (adım → tehlike → önlem, sıra numarası önden
 *   yazılı); Enter satırı kaydeder, alanlar boşalır, art arda giriş sürer.
 * - **KİLİT SEBEBİ GÖRÜNÜR (§2.40):** düzenlenemeyen kartta *"girilmemiş"*
 *   deyip susmak, kilit ile eksikliği ayırt edilemez kılıyordu. Sebep
 *   sunucudan, bayrakla AYNI yüklemden (`jsaKilitSebebi` —
 *   `ptwTaslakIcerikKilidi`); metin `PTW_ITA_KILIT_METNI`nden.
 * - **"Kaldır" ⋮ ALTINDA (P2-a):** geri alınamayan eylem satırda durmaz.
 */
function ItaKarti({ detay }: { detay: PtwPermitDetail }): React.JSX.Element {
  const yaz = useIzinJsa();
  const duzenlenebilir = detay.yetkiler.jsaDuzenleyebilir;
  const kilitSebebi = detay.yetkiler.jsaKilitSebebi;
  const [isAdimi, setIsAdimi] = useState('');
  const [tehlike, setTehlike] = useState('');
  const [onlem, setOnlem] = useState('');

  const mevcut = detay.jsaSatirlari.map((j) => ({
    isAdimi: j.isAdimi,
    tehlike: j.tehlike,
    onlem: j.onlem,
  }));
  const bos = detay.jsaSatirlari.length === 0;
  const dokunuldu = isAdimi !== '' || tehlike !== '' || onlem !== '';
  const kaydedilebilir = !!isAdimi.trim() && !!tehlike.trim() && !!onlem.trim();
  // Madde 221 — yazılmış ama kaydedilmemiş İTA satırıyla çıkış YÖNLENDİRİCİDE uyarılır.
  useKaydedilmemisIcerik(dokunuldu ? 1 : 0, 'İTA satırı');

  function kaydet(): void {
    if (!kaydedilebilir || yaz.isPending) return;
    const gonderilen = { isAdimi: isAdimi.trim(), tehlike: tehlike.trim(), onlem: onlem.trim() };
    yaz.mutate(
      { permitId: detay.permit.id, satirlar: [...mevcut, gonderilen] },
      {
        // ⚠️ **KULLANICININ SONRADAN YAZDIĞI SİLİNMEZ.** Kayıt dönene kadar
        // kullanıcı bir sonraki satırı yazmaya başlamış olabilir; koşulsuz
        // temizlik onu **görünür bir sebep olmadan** silerdi (*"yazdığımı
        // kaybettim"* — §2.40). Alan yalnız **gönderilen değeri hâlâ
        // taşıyorsa** boşalır.
        onSuccess: () => {
          setIsAdimi((simdiki) => (simdiki.trim() === gonderilen.isAdimi ? '' : simdiki));
          setTehlike((simdiki) => (simdiki.trim() === gonderilen.tehlike ? '' : simdiki));
          setOnlem((simdiki) => (simdiki.trim() === gonderilen.onlem ? '' : simdiki));
        },
      },
    );
  }

  return (
    <Bolum
      baslik="İş tehlike analizi (İTA)"
      aciklama="Bu iş için: hangi adımda ne olabilir, buna karşı ne yapılacak. Her izinde sıfırdan yazılır — hazır listeden getirilmez. Zorunlu değildir; boş bırakılan analizi onaylayan reddedebilir."
      testId="izin-ita-karti"
    >
      {(!bos || duzenlenebilir) && (
        <Table data-testid="izin-ita-listesi">
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={40}>#</Table.Th>
              <Table.Th>İş adımı</Table.Th>
              <Table.Th>Tehlike</Table.Th>
              <Table.Th>Önlem</Table.Th>
              {duzenlenebilir && <Table.Th w={44} aria-label="Satır eylemi" />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {detay.jsaSatirlari.map((j, dizin) => (
              <Table.Tr key={j.id} data-testid="izin-ita-satiri">
                <Table.Td>{j.sira}</Table.Td>
                <Table.Td>{j.isAdimi}</Table.Td>
                <Table.Td>{j.tehlike}</Table.Td>
                <Table.Td>{j.onlem}</Table.Td>
                {duzenlenebilir && (
                  <Table.Td>
                    <SatirEylemleri
                      testId={`izin-ita-eylem-${j.sira}`}
                      digerleri={[
                        {
                          etiket: 'Satırı kaldır',
                          tehlikeli: true,
                          pasif: yaz.isPending,
                          testId: 'izin-ita-sil',
                          onClick: () =>
                            yaz.mutate({
                              permitId: detay.permit.id,
                              satirlar: mevcut.filter((_, i) => i !== dizin),
                            }),
                        },
                      ]}
                    />
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
            {duzenlenebilir && (
              <SolukSatir
                testId="izin-ita-soluk"
                dokunuldu={dokunuldu}
                kaydedilebilir={kaydedilebilir}
                kaydediliyor={yaz.isPending}
                kolonSayisi={4}
                onKaydet={kaydet}
              >
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {detay.jsaSatirlari.length + 1}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <TextInput
                    size="xs"
                    placeholder="İş adımı"
                    aria-label="İş adımı"
                    data-testid="izin-ita-adim"
                    value={isAdimi}
                    onChange={(olay) => setIsAdimi(olay.currentTarget.value)}
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    size="xs"
                    placeholder="Tehlike"
                    aria-label="Tehlike"
                    data-testid="izin-ita-tehlike"
                    value={tehlike}
                    onChange={(olay) => setTehlike(olay.currentTarget.value)}
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    size="xs"
                    placeholder="Önlem"
                    aria-label="Önlem"
                    data-testid="izin-ita-onlem"
                    value={onlem}
                    onChange={(olay) => setOnlem(olay.currentTarget.value)}
                  />
                </Table.Td>
              </SolukSatir>
            )}
          </Table.Tbody>
        </Table>
      )}
      {bos && !duzenlenebilir && (
        <Text size="sm" c="dimmed" data-testid="izin-ita-bos">
          İş tehlike analizi girilmemiş.
        </Text>
      )}
      {kilitSebebi && (
        <YazamamaSebebi testId="izin-ita-kilit-sebebi">
          {PTW_ITA_KILIT_METNI[kilitSebebi]}
        </YazamamaSebebi>
      )}
      {yaz.error && (
        <Text size="sm" c={ANLAM.engel} role="alert" data-testid="izin-ita-hata">
          {yaz.error.message}
        </Text>
      )}
    </Bolum>
  );
}
